/**
 * Opens the in-app User Guide (`/kaveri-lite-help.html`) in a new tab.
 *
 * Lives here rather than in a component because two places open the guide: the
 * global top-bar button (side-navbar) and the self-service shell's own "How to
 * use this" deep links from What's New. Keeping one implementation means the
 * cache-busting and blob handling can't drift between them.
 *
 * The guide is fetched and re-served from a blob URL (rather than linked
 * directly) so an `anchor` can be applied on load: the injected script sets
 * `location.hash`, and the guide's own handler opens the matching tab before
 * scrolling — a plain `href="...#anchor"` can't do that, because the target may
 * sit inside an inactive (display:none) tab panel.
 *
 * @param anchor Optional guide section id to jump to, without the leading '#'.
 */
export function openUserGuide(anchor?: string): void {
  // `?v=` + no-store: the guide is a static asset that gets updated in place, so
  // a cached copy would keep showing stale documentation after a deploy.
  fetch(`/kaveri-lite-help.html?v=${Date.now()}`, { cache: 'no-store' })
    .then((response) => response.text())
    .then((html) => {
      const hashScript = anchor
        ? `<script>location.hash=${JSON.stringify('#' + anchor)};</script>`
        : '';
      const blob = new Blob([html + hashScript], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener');
      if (win) {
        win.addEventListener('unload', () => URL.revokeObjectURL(url));
      } else {
        // Pop-up blocked — nothing will read the blob, so don't leak it.
        URL.revokeObjectURL(url);
      }
    })
    .catch((error) => console.warn('[user-guide] could not open the guide:', error));
}
